import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import getNotePartType from 'notebook/notes/util/getNotePartType';
import * as StoreTransformUtil from 'notebook/store/StoreTransformUtil';
import TFIConstants, {TFIFileMeta} from 'notebook/tfi/TFIConstants';
import {
  ExternalFile,
  ExternalFileExcerpt,
  FromServerNoteLinkedFile,
  NoteLinkedFile,
  NotePart,
  NotePartItem,
  NotePartType,
} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';

import {CidIdMap} from '../NoteConstants';

export function prepareForStore(noteLinkedFile: NoteLinkedFile, cidIdMap: CidIdMap): void {
  StoreTransformUtil.addCid(noteLinkedFile, 'note-linked-file-', cidIdMap);
}

/**
 * Update the external file / excerpt found on a NoteLinkedFile, while preserving name.
 *
 * Note that we can't just update 'externalFile' on the NoteLinkedFile, but instead insert an updated version
 * of the NoteLinkedFile since:
 *   a) external files inherit permissions from the NoteLinkedFile they are attached to, and
 *   b) NoteLinkedFiles are made accessible via the UUID,
 */
function replaceExternalFile(
  notePart: NotePart,
  newNoteLinkedFile: FromServerNoteLinkedFile,
  cidIdMap: CidIdMap = {},
  externalFileExcerpt: ExternalFileExcerpt | null = null
): NotePart {
  // Register new NLF with store
  const cid = _.uniqueId('note-linked-file');
  cidIdMap[cid] = newNoteLinkedFile.id;

  const updatedNoteLinkedFile = {
    // The id is captured in the cidIdMap, and can be looked up using the above cid
    // eslint-disable-next-line no-restricted-syntax
    ..._.omit(newNoteLinkedFile, 'id'),
    cid,
    // If we are setting the NLF on a note part with an existing NLF, preserve the name.
    name:
      getNotePartType(notePart) === ModelTypeConstants.NOTE_LINKED_FILE
        ? (notePart.item as NoteLinkedFile).name
        : newNoteLinkedFile.name,
    // Only preserve the TFIFileMeta on the original (not on excerpts)
    tfiFileMeta: externalFileExcerpt ? null : newNoteLinkedFile.tfiFileMeta,
    externalFileExcerpt,
  };
  return {
    ...notePart,
    item: updatedNoteLinkedFile,
    // The note part has a NoteLinkedObject attached (which has item.type === NOTE_LINKED_FILE via JTI)
    type: NotePartType.NOTE_LINKED_OBJECT,
  };
}

export function isLinkedToExternalFile(notePart: NotePart, targetExternalFile: ExternalFile): boolean {
  if (getNotePartType(notePart) !== ModelTypeConstants.NOTE_LINKED_FILE) {
    return false;
  }
  const {externalFile} = notePart.item as NoteLinkedFile;
  return Boolean(externalFile && externalFile.id === targetExternalFile.id);
}

export function hasExcerptOfExternalFile(notePart: NotePart, targetExternalFile: ExternalFile): boolean {
  return Boolean(
    isLinkedToExternalFile(notePart, targetExternalFile) &&
    (notePart.item as NoteLinkedFile).externalFileExcerpt
  );
}

export function getExcerptOffsets(
  noteParts: Array<NotePart>,
  targetExternalFile: ExternalFile
): Array<number> {
  const offsets = [];
  for (const notePart of noteParts) {
    if (
      isLinkedToExternalFile(notePart, targetExternalFile) &&
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      (notePart.item as NoteLinkedFile).externalFileExcerpt !== null
    ) {
      offsets.push((notePart.item as NoteLinkedFile).externalFileExcerpt!.referenceOffset);
    }
  }
  return offsets;
}

function setAsPlaceholder(notePart: NotePart): NotePart {
  return {
    ...notePart,
    item: null,
    item_id: null,
    placeholder: true,
  };
}

/**
 * Update a notePart if its NoteLinkedFile wraps `oldExternalFile` and replace it with the one
 * wrapped by newNoteLinkedFile.
 *
 * If the notePart's NLF is an excerpt, additionally replace the old excerpt with the corresponding one
 * (based on offset) on the new excerpt, inserting a placeholder if that offset is now out-of-bounds.
 *
 * @param {object} notePart - note part to potentially update
 * @param {object} currentNotePart - current note part holding the target external file to be replaced
 * @param {object} newNoteLinkedFile - NLF whose externalFile is desired to be set on `notePart`
 * @param {object[]?} excerpts - an array of excerpts for newNoteLinkedFile.externalFile, is null when there
 *  are no excerpts to update
 * @param {object} cidIdMap - new cid/ids are registered into this object, caller to register with store
 * @return {object} - new note part to replace original `notePart`
 */
export function maybeReplaceExternalFile(
  notePart: NotePart,
  currentNotePart: NotePart,
  newNoteLinkedFile: FromServerNoteLinkedFile,
  excerpts: Array<ExternalFileExcerpt> | null,
  cidIdMap: CidIdMap
): NotePart {
  const oldExternalFile = (currentNotePart.item as NoteLinkedFile).externalFile;
  if (oldExternalFile && !isLinkedToExternalFile(notePart, oldExternalFile)) {
    return notePart;
  }
  if (!(notePart.item as NoteLinkedFile).externalFileExcerpt) {
    if (notePart.cid === currentNotePart.cid) {
      return replaceExternalFile(notePart, newNoteLinkedFile, cidIdMap);
    } else {
      return notePart;
    }
  }
  if (!excerpts) {
    // When excerpts is null, there are no excerpts to update so return the original note part
    return notePart;
  }
  const {referenceOffset} = (notePart.item as NoteLinkedFile).externalFileExcerpt!;
  const excerpt = excerpts[referenceOffset];
  if (excerpt) {
    return replaceExternalFile(notePart, newNoteLinkedFile, cidIdMap, excerpt);
  }
  notifications.warn(
    `**${newNoteLinkedFile.name}** *p. ${referenceOffset + 1}* no longer exists and was removed.`
  );
  return setAsPlaceholder(notePart);
}

export function getTFIFileMeta(notePartItem: NotePartItem | null): TFIFileMeta | null {
  // We don't expect any excerpt to have a TFI file, but it's not currently constrained via the DB,
  // so we defensively ignore any tfiFileMetas.
  // eslint-disable-next-line benchlint/no-in-operator
  if (notePartItem == null || ('externalFileExcerpt' in notePartItem && notePartItem.externalFileExcerpt)) {
    return null;
  }
  // eslint-disable-next-line benchlint/no-in-operator
  return 'tfiFileMeta' in notePartItem ? notePartItem.tfiFileMeta : null;
}

export function isLocalSyncFile(notePartItem: NotePartItem | null): boolean {
  // Returns a boolean representing whether the file is checked out locally via desktop sync.
  const tfiFileMeta = getTFIFileMeta(notePartItem);
  return Boolean(
    BenchlingConfig.ELN_ENABLE_DESKTOP_SYNC &&
    tfiFileMeta &&
    tfiFileMeta.tfiAccount.accountType === TFIConstants.ACCOUNT_TYPE.HOSTED_S3 &&
    tfiFileMeta.thirdpartyFileId.startsWith('.local-sync/')
  );
}
